/**
 * Login screen (P1-T11): magic-link request form + "check your email" state.
 * Public route — no top bar or bottom nav.
 */
import { api, ApiError } from '../api/client.js';
import { toast } from '../ui/toast.js';
import { escapeHtml } from '../components/placeholder.js';
import { bindGoogleButton, googleButtonHtml } from '../components/googleButton.js';

const OAUTH_ERRORS: Record<string, string> = {
  account_deleted: 'That account was deleted. Sign in with a different Google account.',
  oauth_not_implemented: 'Google sign-in is not available right now. Use your email instead.',
};

export function renderLogin(root: HTMLElement): void {
  root.innerHTML = `
    <div class="login-wrap">
      <div class="stack login-card">
        <div class="wordmark wordmark-lg">Loomia</div>
        <p class="muted" style="text-align:center;">AI Creative Studio — sign in to start creating.</p>
        ${googleButtonHtml()}
        <form id="login-form" class="stack" novalidate>
          <label class="field">
            <span class="field-label">Email</span>
            <input id="login-email" class="input" type="email" name="email"
                   placeholder="you@example.com" autocomplete="email" required />
          </label>
          <button class="btn btn-primary btn-block" type="submit" id="login-submit">
            Send sign-in link
          </button>
        </form>
        <p class="muted" style="font-size: var(--fs-xs); text-align: center;">
          We'll email you a one-tap sign-in link. No password needed.
        </p>
        <a class="muted" href="#/home" style="text-align:center; text-decoration: underline;">Continue browsing</a>
      </div>
    </div>
  `;

  bindGoogleButton(root);
  // Back from a failed Google sign-in: say why, once.
  const oauthError = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('oauth_error');
  if (oauthError) {
    toast(OAUTH_ERRORS[oauthError] ?? 'Google sign-in did not finish. Please try again.');
    window.history.replaceState(null, '', '#/login');
  }

  const form = root.querySelector<HTMLFormElement>('#login-form');
  const emailInput = root.querySelector<HTMLInputElement>('#login-email');
  const submit = root.querySelector<HTMLButtonElement>('#login-submit');

  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    const email = (emailInput?.value ?? '').trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast('Please enter a valid email address.');
      emailInput?.focus();
      return;
    }
    if (submit) submit.disabled = true;
    api
      .requestMagicLink(email)
      .then((res) => renderLoginSent(root, email, res.devMagicUrl))
      .catch((err: unknown) => {
        if (submit) submit.disabled = false;
        if (err instanceof ApiError) toast(err.message);
        else toast('Could not send the link. Check your connection and try again.');
      });
  });
}

function renderLoginSent(root: HTMLElement, email: string, devMagicUrl?: string): void {
  root.innerHTML = `
    <div class="login-wrap">
      <div class="stack login-card" style="align-items:center; text-align:center;">
        <div class="empty-icon">✉️</div>
        <h1 class="page-title" style="font-size: var(--fs-xl);">Check your email</h1>
        <p class="muted" style="max-width: 300px;">
          We sent a sign-in link to <strong class="credit">${escapeHtml(email)}</strong>.
          It expires in 15 minutes.
        </p>
        ${
          devMagicUrl
            ? `<a class="btn btn-primary btn-block" href="${escapeHtml(devMagicUrl)}">Open sign-in link</a>
               <p class="muted" style="font-size: var(--fs-xs);">Dev mode: no email provider is configured yet, so no email is sent.</p>`
            : ''
        }
        <button class="btn btn-secondary btn-block" id="login-back" type="button">
          Use a different email
        </button>
      </div>
    </div>
  `;
  root.querySelector('#login-back')?.addEventListener('click', () => renderLogin(root));
}
